"use client";

import { useState } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";
import { PANEL_URL } from "@/lib/panelPrices";
import { textList, type PageTexts } from "@/lib/siteTexts";

// Wording comes from Admin -> Тексты страниц (start-trial.*).
const resourceStyles = [
  { icon: "ServerIcon", color: "#00D4FF" },
  { icon: "CircleStackIcon", color: "#4F7CFF" },
  { icon: "GlobeAltIcon", color: "#7C3AED" },
  { icon: "WifiIcon", color: "#00FF88" },
  { icon: "ShieldCheckIcon", color: "#F59E0B" },
  { icon: "HeadphoneIcon", color: "#EC4899" },
] as const;

type Texts = PageTexts<"start-trial">;

export default function StartTrial({ texts: t }: { texts: Texts }) {
  const resources = resourceStyles.map((style, i) => {
    const n = (i + 1) as 1 | 2 | 3 | 4 | 5 | 6;
    return {
      ...style,
      title: t[`resources.item${n}.title`],
      description: t[`resources.item${n}.value`],
      detail: t[`resources.item${n}.detail`],
    };
  });
  const steps = ([1, 2, 3, 4] as const).map((n) => ({
    num: String(n).padStart(2, "0"),
    title: t[`steps.step${n}.title`],
    desc: t[`steps.step${n}.text`],
  }));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRegister = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = { mode: "trial" };
      const response = await fetch(`${PANEL_URL}/api/signup-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
        redirect: "manual",
      });

      if (response.ok) {
        const data = await response.json();
        const token = data.token;
        window.location.assign(
          `${PANEL_URL}/auth/redirect/saml2?intent=${encodeURIComponent(token)}`
        );
      } else {
        setError(t["cta.error"]);
        setLoading(false);
      }
    } catch (err) {
      setError(t["cta.offline"]);
      setLoading(false);
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-[rgba(0,212,255,0.05)] rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-[rgba(79,124,255,0.06)] rounded-full blur-[80px] pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass neon-border mb-6">
            <span className="status-dot status-operational" />
            <span className="text-xs font-semibold tracking-wider text-[#00D4FF] uppercase">{t["hero.badge"]}</span>
          </div>

          <h1 className="font-display font-bold text-5xl lg:text-6xl leading-tight text-white mb-6">
            <span className="gradient-text">{t["hero.title1"]}</span>
            <br />
            {t["hero.title2"]}
          </h1>

          <p className="text-[#8BA3CC] text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            {t["hero.subtitle"]}
          </p>

          <div className="inline-flex items-center gap-4 glass rounded-2xl px-8 py-5 neon-border mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,212,255,0.15)" }}>
              <Icon name="GiftIcon" size={24} className="text-[#00D4FF]" />
            </div>
            <div className="text-left">
              <div className="text-2xl font-bold text-white">{t["hero.bonus.amount"]} <span className="gradient-text">{t["hero.bonus.currency"]}</span></div>
              <div className="text-sm text-[#8BA3CC]">{t["hero.bonus.note"]}</div>
            </div>
            <div className="w-px h-10 bg-[rgba(79,124,255,0.3)]" />
            <div className="text-left">
              <div className="text-sm text-[#8BA3CC]">{t["hero.bonus.side"]}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-16 relative">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="section-label mb-3 block">{t["resources.label"]}</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl text-white">
              {t["resources.title"]}{" "}
              <span className="gradient-text">{t["resources.titleAccent"]}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {resources.map((res, i) => (
              <div
                key={i}
                className="glass rounded-2xl p-6 neon-border-blue card-hover group transition-all duration-300 hover:border-[rgba(0,212,255,0.4)]"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ background: `${res.color}18` }}
                >
                  <Icon name={res.icon} size={22} style={{ color: res.color }} />
                </div>
                <h3 className="font-bold text-white text-lg mb-1">{res.title}</h3>
                <p className="text-[#00D4FF] font-semibold text-sm mb-2">{res.description}</p>
                <p className="text-[#4A6080] text-sm">{res.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 relative">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="section-label mb-3 block">{t["steps.label"]}</span>
            <h2 className="font-display font-bold text-3xl lg:text-4xl text-white">
              {t["steps.title"]} <span className="gradient-text">{t["steps.titleAccent"]}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <div key={step.num} className="relative">
                {idx < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-[rgba(0,212,255,0.4)] to-transparent z-0" />
                )}
                <div className="glass rounded-2xl p-6 neon-border text-center relative z-10">
                  <div className="text-3xl font-display font-bold gradient-text mb-3">{step.num}</div>
                  <h3 className="font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-[#4A6080] text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="glass rounded-3xl p-8 md:p-12 neon-border relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-neon" />
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="section-label mb-3 block">{t["terms.label"]}</span>
                <h2 className="font-display font-bold text-3xl text-white mb-4">
                  {t["terms.title"]} <span className="gradient-text">{t["terms.titleAccent"]}</span>
                </h2>
                <ul className="space-y-3">
                  {textList(t["terms.points"], "\n").map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Icon name="CheckCircleIcon" size={18} className="text-[#00FF88] mt-0.5 shrink-0" />
                      <span className="text-[#8BA3CC] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-4">
                <div className="glass rounded-2xl p-5 neon-border-blue">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="ClockIcon" size={18} className="text-[#00D4FF]" />
                    <span className="font-semibold text-white">{t["terms.card1.title"]}</span>
                  </div>
                  <p className="text-[#4A6080] text-sm">{t["terms.card1.text"]}</p>
                </div>
                <div className="glass rounded-2xl p-5 neon-border-blue">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="CurrencyDollarIcon" size={18} className="text-[#00FF88]" />
                    <span className="font-semibold text-white">{t["terms.card2.title"]}</span>
                  </div>
                  <p className="text-[#4A6080] text-sm">{t["terms.card2.text"]}</p>
                </div>
                <div className="glass rounded-2xl p-5 neon-border-blue">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon name="BoltIcon" size={18} className="text-[#4F7CFF]" />
                    <span className="font-semibold text-white">{t["terms.card3.title"]}</span>
                  </div>
                  <p className="text-[#4A6080] text-sm">{t["terms.card3.text"]}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-[rgba(0,212,255,0.05)] rounded-full blur-[100px] pointer-events-none" />
        <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-display font-bold text-4xl text-white mb-4">
            {t["cta.title"]}
          </h2>
          <p className="text-[#8BA3CC] mb-8">
            {t["cta.subtitle"]}
          </p>

          {error && (
            <div className="mb-6 flex items-center gap-3 glass rounded-xl px-5 py-4 border border-red-500/30 text-red-400 text-sm">
              <Icon name="ExclamationCircleIcon" size={18} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={handleRegister}
              disabled={loading}
              className="btn-primary text-base font-bold px-10 py-4 rounded-xl flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t["cta.wait"]}
                </>
              ) : (
                <>
                  <Icon name="RocketLaunchIcon" size={20} />
                  {t["cta.register"]}
                </>
              )}
            </button>
            <Link
              href={t["cta.secondary.href"]}
              className="btn-secondary text-base font-semibold px-8 py-4 rounded-xl flex items-center gap-2"
            >
              <Icon name="CalculatorIcon" size={20} />
              {t["cta.secondary.label"]}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
